import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const ManageOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [stats, setStats] = useState({
        total_orders: 0,
        total_revenue: 0,
        pending_orders: 0,
        completed_orders: 0
    });


    // Pagination
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);

    /** Debounce search */
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    /** Refetch whenever filters or page change */
    useEffect(() => {
        fetchOrders(1); // reset to first page when search/filter changes
    }, [debouncedSearch, statusFilter]);

    const fetchOrders = async (pageNumber = 1) => {
        try {
            setLoading(true);

            const params = {
                page: pageNumber,
                search: debouncedSearch || undefined,
                payment_status: statusFilter !== 'all' ? statusFilter : undefined,
            };

            const response = await api.get('/admin/orders', { params });
            const paginator = response.data.data.orders;
            const statsData = response.data.data.stats;

            setOrders(paginator.data || []);
            setPagination(paginator);
            setPage(pageNumber);
            setStats(statsData);
        } catch (error) {
            toast.error('Failed to load orders');
            console.error('Error fetching orders:', error);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    /** Pagination UI */
    const renderPagination = () => {
        if (!pagination.last_page || pagination.last_page <= 1) return null;

        const current = pagination.current_page;
        const last = pagination.last_page;
        const maxPages = 3;

        let start = Math.max(1, current - Math.floor(maxPages / 2));
        let end = start + maxPages - 1;

        if (end > last) {
            end = last;
            start = Math.max(1, end - maxPages + 1);
        }

        const pageNumbers = [];
        for (let i = start; i <= end; i++) pageNumbers.push(i);

        return (
            <nav className="mt-4">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => fetchOrders(current - 1)}>Previous</button>
                    </li>

                    {start > 1 && (
                        <>
                            <li className="page-item"><button className="page-link" onClick={() => fetchOrders(1)}>1</button></li>
                            {start > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                        </>
                    )}

                    {pageNumbers.map(num => (
                        <li key={num} className={`page-item ${num === current ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => fetchOrders(num)}>{num}</button>
                        </li>
                    ))}

                    {end < last && (
                        <>
                            {end < last - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                            <li className="page-item"><button className="page-link" onClick={() => fetchOrders(last)}>{last}</button></li>
                        </>
                    )}

                    <li className={`page-item ${current === last ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => fetchOrders(current + 1)}>Next</button>
                    </li>
                </ul>
            </nav>
        );
    };

    const formatCurrency = (amount) => `$${parseFloat(amount).toFixed(2)}`;
    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });

    const getPaymentStatusBadge = (status) => {
        const badges = {
            pending: 'badge-warning',
            paid: 'badge-success',
            failed: 'badge-danger',
            refunded: 'badge-info'
        };
        return badges[status] || 'badge-secondary';
    };

  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-900 font-weight-bold">Manage Orders</h1>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-primary shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                    Total Orders
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {stats.total_orders}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-shopping-cart fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-success shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                    Paid Orders
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {stats.completed_orders}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-check-circle fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-warning shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                    Pending Orders
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {stats.pending_orders}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-clock fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-info shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                    Total Revenue
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {formatCurrency(stats.total_revenue)}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-dollar-sign fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card shadow mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Search by order number, customer name or email..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                    }}
                />
            </div>
            <div className="col-md-3">
                <select
                    className="form-control"
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                    }}
                >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                </select>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-gray-900">All Orders ({orders.length})</h6>
        </div>
        <div className="card-body">
            {loading ? (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
          <div className="table-responsive">
            <table className="table table-bordered" width="100%" cellSpacing="0">
              <thead>
                <tr style={{ fontSize: '0.8rem' }}>
                  <th>Order #</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Event</th>
                  <th>Organizer</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Payment Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.length > 0 ? (
                  orders.map(order => (
                    <tr key={order.id} style={{ fontSize: '0.85rem' }}>
                      <td className="font-weight-bold text-primary">
                        #{order.id}
                      </td>
                      <td>{formatDate(order.created_at)}</td>
                      <td>
                        {order.user ? (
                          <div>
                            <div className="font-weight-bold">{order.user.first_name} {order.user.last_name}</div>
                            <small className="text-muted">{order.user.email}</small> <br/>
                              <small className="text-muted">{order.user.mobile}</small>
                          </div>
                        ) : 'N/A'}
                      </td>
                      <td>
                        <div className="text-truncate" style={{ maxWidth: '200px' }}>
                          {order.event?.title || 'N/A'}
                        </div>
                      </td>
                      <td>
                        <small className="text-muted">
                          {order.event?.organizer?.name || 'N/A'}
                        </small>
                      </td>
                      <td>
                        <div>
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="small">
                              {item.quantity}x {item.ticket_category?.category_name}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="font-weight-bold">
                        {parseFloat(order.total_amount - order.discount_total).toFixed(2)} {order.event.currency}
                      </td>
                      <td>
                        <span className={`badge ${getPaymentStatusBadge(order.payment_status)}`}>
                          {order.payment_status}
                        </span>
                        <div className="small text-muted">{order.payment_method}</div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      {searchTerm ? 'No orders found matching your search' : 'No orders yet'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
              {renderPagination()}
          </div>
                )}
        </div>
      </div>
    </div>
  );
};

export default ManageOrders;

